'use client';

// ═══ НАБОР ЭЛЕМЕНТОВ ФОРМЫ КАБИНЕТА (заказ владельца 2026-08-08; пузыри и их
// содержимое — ТОЛЬКО отсюда, владелец 2026-08-11) ═══
//
// ЖЕЛЕЗНОЕ: разметку формы РУКАМИ БОЛЬШЕ НЕ ПИШУТ — ни поле, ни ПУЗЫРЬ, ни его
// содержимое. Никаких своих `<div className="fld">`, своих `card fld-card`, своих
// kf-blk, своих подписей, подсказок и красных строк на странице — каждый элемент
// берётся отсюда. Причина: одинаковые по смыслу формы разошлись видом (у мастера
// шаблона одни пузыри, у окон конструктора другие, на каждой странице свои) — «зоопарк»,
// пойманный владельцем 11.08 на соседних формах «Старт»/«Заметка»/«О сценарии».
//
// Эталон вида — форма мастера шаблона («Бот на кодовое слово»): серый заголовок НАД
// пузырём, белый скруглённый пузырь (радиус и тень пузыря из tokens.css), внутри —
// значение и подсказка; выбор из вариантов — раскрытым списком строк с галочкой
// (KChoice), настройка-вкл/выкл — строкой с тумблером. Числа и вид задаёт ОДИН набор
// правил (kit.css, блок «ШАГ МАСТЕРА», скоуп `:is(.wiz-pane, .fk-pane)` + cabinet.css
// «Формы строками» .iosf) — здесь только разметка, своих стилей набор не заводит.
//
// Правило подкреплено замком: ESLint роняет сборку на самодельных пузырях
// (`fld-card` / `kf-blk` / `wiz-pane` / `fk-pane` в className вне этого файла) —
// eslint.config.mjs, FORM_BUBBLE_RULES; уже написанное — eslint.kit-legacy.mjs
// (FORM_LEGACY_FILES, список только сокращается).
//
// Канон: docs/concepts/UI-TEMPLATE-CANON.md, раздел «Элементы формы».
import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import { useDrawerOptional } from './drawer';
import { isAppClient, readLayoutMode } from './layout-mode';
import { KBareButton, KChoice, KChoiceMany, KInput } from '@scriptera/ui/kit';

// Контролы и раскрытые списки — из набора @scriptera/ui/kit; форма собирается ОДНИМ
// импортом отсюда, чтобы не тянуть контролы мимо набора.
//
// ТУМБЛЕРЫ (урок владельца 2026-08-11, приёмка этапа 0): имя тумблера ОСТАЁТСЯ ПРИ НЁМ
// (жирным в строке KSwitch: label + hint + переключатель), а пузырю придумывается ИМЯ
// ГРУППЫ; несколько тумблеров одной темы — ОДИН FBlock. Компонент «пузырь на каждый
// тумблер» (FSwitchRow) был ошибкой и удалён: он крал имя тумблера в заголовок пузыря.
export {
  KBareButton,
  KButton,
  KCheckbox,
  KInput,
  KSelect,
  KTextarea,
  KChoice,
  KChoiceMany,
  KSwitch,
} from '@scriptera/ui/kit';

/**
 * КОРЕНЬ ФОРМЫ. Даёт форме эталонные правила пузырей мастера (скоуп .fk-pane в kit.css —
 * тот же блок правил, что у .wiz-pane мастера) + мобильный вид «строками» (.iosf).
 * Любая форма конструктора/кабинета начинается с него; без корня пузыри не красятся.
 * `wiz` — корень МАСТЕРА шаблона (единственный пользователь — template-wizard):
 * правила вида те же, но за имя `.wiz-pane` держатся CSS-контексты вокруг мастера
 * (`.dr-body:has(> .wiz-pane)`, native-layer, планка низа) — потому у прародителя
 * эталона свой класс, и живёт он тоже здесь, а не строкой на странице.
 */
export function FPane({
  children,
  className,
  wiz,
  fit,
}: {
  children: ReactNode;
  className?: string;
  wiz?: boolean;
  /** ПОЛУШТОРКА ПО ВЫСОТЕ СОДЕРЖИМОГО (UT 14.10 п.30, владелец): короткий лист в браузере на телефоне
   *  встаёт ровно по своему телу — верх листа = шапка + тело + нижний отступ тела листа. Открывать
   *  с `sheet` и стартовым `sheetDetent` (webOnly); здесь он уточняется замером и следит за ростом тела
   *  (ошибка под полем). Широкий экран и приложение — без изменений (там детент не читается). */
  fit?: boolean;
}) {
  const ref = useSheetFit(!!fit);
  return (
    <div ref={ref} className={`${wiz ? 'wiz-pane' : 'fk-pane'} iosf${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}

/** Замер для `FPane fit`: доля экрана от верха листа до низа тела плюс нижний отступ прокрутки листа. */
function useSheetFit(on: boolean) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Только браузер: в приложении листы ведёт натив (детент задаётся при открытии), а без окна
  // (страница вне шторки) замерять нечего — провайдер необязателен.
  const update = useDrawerOptional()?.update;
  useEffect(() => {
    const el = ref.current;
    if (!on || !el || !update || isAppClient() || readLayoutMode() !== 'phone') return;
    const aside = el.closest<HTMLElement>('.drawer.sheet');
    if (!aside) return;
    let last = 0;
    const measure = () => {
      const scroller = el.parentElement;
      const pad = scroller ? parseFloat(getComputedStyle(scroller).paddingBottom) || 0 : 0;
      const h = el.getBoundingClientRect().bottom - aside.getBoundingClientRect().top + pad;
      const vh = window.innerHeight || 1;
      const d = Math.min(0.9, Math.ceil((h / vh) * 100) / 100);
      if (d > 0 && Math.abs(d - last) >= 0.01) {
        last = d;
        update({ sheetDetent: d });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [on, update]);
  return ref;
}

/**
 * ПУЗЫРЬ С НАЗВАНИЕМ — единственный способ положить содержимое в пузырь. Правило
 * владельца (2026-08-11, продиктовано трижды) ЗАШИТО В КОМПОНЕНТ: `label` обязателен —
 * безымянного пузыря этим компонентом собрать НЕЛЬЗЯ. Внутри пузыря каждое поле несёт
 * свой заголовок (FIn); единственное поле, чьё имя совпадает с названием пузыря
 * (эталон «Кодовые слова»), — без FIn.
 */
export function FBlock({
  label,
  lead,
  hint,
  error,
  bad,
  className,
  children,
}: {
  /** Название пузыря — серым над пузырём. Пусто (null) — заголовка нет вовсе: так стоят
   *  пузыри, у которых имя уже сказано шапкой листа (правило владельца 10.09.2026 —
   *  разговорных заголовков над пузырями не бывает; убрали — не подменяем другим). */
  label: ReactNode;
  /** ПОДВОДКА НАД пузырём (под названием): что сделать ДО строк пузыря — тот же класс, что у
   *  подводки FNotifyList (`kf-blk__lead`). Лист «Добавить канал», UT 14.10 п.30. */
  lead?: ReactNode;
  /**
   * Серая подсказка ко ВСЕМУ пузырю — стоит ПОД пузырём (владелец 2026-08-14): описание
   * относится к пузырю целиком, а лёжа внутри читалось как ещё одна строка содержимого.
   * Описание ОТДЕЛЬНОГО ПОЛЯ этим не затронуто — оно остаётся при поле (FIn).
   */
  hint?: ReactNode;
  error?: ReactNode;
  bad?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`fld kf-blk${bad ? ' wiz-bad' : ''}${className ? ` ${className}` : ''}`}>
      {/* ИМЯ ПУЗЫРЯ МОЖЕТ БЫТЬ ПУСТЫМ (владелец 10.09.2026): там, где заголовок над
          пузырём убран, пустой <label> оставлял над ним воздух своей строки. Пусто —
          элемента нет вовсе, ровно как у родного листа (pickLabel не послан). */}
      {label ? <label className="kf-blk__cap">{label}</label> : null}
      {lead ? <p className="meta kf-blk__lead">{lead}</p> : null}
      <div className="kf-blk__body">{children}</div>
      {hint ? <p className="meta kf-blk__note">{hint}</p> : null}
      {error ? <FError>{error}</FError> : null}
    </div>
  );
}

/**
 * ВЫБОР ИЗ ВАРИАНТОВ — заголовок над пузырём + раскрытый список строк с галочкой
 * (эталон «Что запускает сценарий»/«Аккаунт» мастера). Выпадашку сюда не ставить:
 * варианты с пояснением живут строками (владелец 2026-08-10). Больше шести вариантов —
 * SmartSelect в FBlock.
 */
export function FChoiceBlock({
  label,
  value,
  options,
  onChange,
  hint,
  error,
  bad,
}: {
  label: ReactNode;
  value: string;
  options: readonly { value: string; label: ReactNode; hint?: ReactNode; disabled?: boolean }[];
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: ReactNode;
  bad?: boolean;
}) {
  return (
    <FBlock label={label} hint={hint} error={error} bad={bad}>
      <KChoice
        value={value}
        options={options}
        onChange={onChange}
        ariaLabel={typeof label === 'string' ? label : undefined}
      />
    </FBlock>
  );
}

/**
 * ВКЛАДКИ ВНУТРИ ПУЗЫРЯ (У4/У5 плана условий; эталон ЧП — вкладки «Длительность | Дата»
 * над полями «Задержки», скрин 20). Нужны там, где варианты меняют САМИ ПОЛЯ под ними
 * («После» — одно время, «Между» — два): раскрытым списком с галочкой (FChoiceBlock) это
 * читалось бы как ещё одна настройка, а не как переключение формы.
 *
 * Вид — канон вкладок кабинета (владелец 2026-07-13): подчёркивание фирменным цветом,
 * без пилюль и серого трека (`.line-tabs` в cabinet.css); сегменты `.seg` — только для
 * двух положений тумблера. Кнопка — KBareButton набора: своих `<button>` в кабинете нет.
 */
export function FTabs({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: readonly { value: string; label: ReactNode }[];
  onChange: (value: string) => void;
  ariaLabel?: string;
}) {
  return (
    <div
      className="line-tabs"
      role="tablist"
      aria-label={typeof ariaLabel === 'string' ? ariaLabel : undefined}
    >
      {options.map((o) => (
        <KBareButton
          key={o.value}
          role="tab"
          aria-selected={value === o.value}
          className={value === o.value ? 'on' : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </KBareButton>
      ))}
    </div>
  );
}

/**
 * ВЫБОР СТРОКАМИ ВНУТРИ ПУЗЫРЯ (владелец 13.08, доводка панели условий): тот же раскрытый
 * список с галочкой, что у FChoiceBlock, но как ПОЛЕ внутри чужого пузыря — со своим
 * серым заголовком, как у FIn. Нужен там, где пузырь принадлежит не полю, а целому
 * условию (FRowBlock): отдельным пузырём выбор рассыпал бы условие на несколько карточек,
 * а «пузырь в пузыре» запрещён. Самостоятельный выбор на экране — по-прежнему FChoiceBlock.
 */
export function FChoiceIn({
  label,
  value,
  options,
  onChange,
  hint,
  error,
}: {
  label: ReactNode;
  value: string;
  options: readonly { value: string; label: ReactNode; hint?: ReactNode; disabled?: boolean }[];
  onChange: (value: string) => void;
  hint?: ReactNode;
  error?: ReactNode;
}) {
  return (
    <FIn label={label} hint={hint} error={error}>
      <KChoice
        value={value}
        options={options}
        onChange={onChange}
        ariaLabel={typeof label === 'string' ? label : undefined}
      />
    </FIn>
  );
}

/**
 * ПУЗЫРЬ ОДНОГО УСЛОВИЯ (владелец 13.08: «каждое условие группы — свой пузырь; заголовок
 * пузыря = значок, имя и корзина; поля и пояснение внутри»). От обычного FBlock отличается
 * только заголовком: он не подпись поля, а ШАПКА условия с органами управления самим
 * условием (корзина; у меток — «Является / Не является»).
 *
 * Внутри — поля через FIn/FChoiceIn и пояснение «Условие выполнено, если …» последней
 * строкой: своих пузырей внутри не заводить («пузырь в пузыре» запрещён, владелец 11.08).
 */
export function FRowBlock({
  icon,
  label,
  right,
  onRemove,
  removeLabel,
  error,
  children,
}: {
  /** Значок вида условия (RowGlyph страницы) — слева от имени. */
  icon?: ReactNode;
  label: ReactNode;
  /** Разовый выбор шапки (PopSelect «Является / Не является» у меток). */
  right?: ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="fld kf-blk">
      <div className="kf-blk__cap fk-rowcap">
        {icon ? (
          <span className="fk-rowcap__ic" aria-hidden>
            {icon}
          </span>
        ) : null}
        <b className="fk-rowcap__t">{label}</b>
        {right}
        {onRemove ? (
          <KBareButton
            className="fk-rowcap__del"
            onClick={onRemove}
            aria-label={removeLabel}
            title={removeLabel}
          >
            <svg
              viewBox="0 0 24 24"
              width={15}
              height={15}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" />
            </svg>
          </KBareButton>
        ) : null}
      </div>
      <div className="kf-blk__body">{children}</div>
      {error ? <FError>{error}</FError> : null}
    </div>
  );
}

/**
 * ПУНКТИРНЫЙ ПУЗЫРЬ «добавить…» внизу формы (владелец 13.08: «Добавить условие» — как
 * «+ Действие» в других местах). Не кнопка-плитка: тот же радиус и та же ширина, что у
 * пузырей формы, только пунктирная рамка вместо белого фона.
 */
export function FAddBubble({
  label,
  onClick,
  disabled,
}: {
  label: ReactNode;
  onClick: () => void;
  /** ДОБАВЛЯТЬ НЕЧЕГО (К20.1 п.15, владелец 07.09.2026: «добавлены все триггеры, и кнопка
   *  „Добавить триггер“ должна стать неактивной»): пузырь на месте, но приглушён и не
   *  нажимается. Вид неактивного — общий для набора (`.k-bare:disabled`), своего нет. */
  disabled?: boolean;
}) {
  return (
    <KBareButton className="fk-add" onClick={onClick} disabled={disabled}>
      <svg
        viewBox="0 0 24 24"
        width={15}
        height={15}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        aria-hidden
      >
        <path d="M12 5v14M5 12h14" />
      </svg>
      {label}
    </KBareButton>
  );
}

/** Тот же блок-выбор, но отмечается НЕСКОЛЬКО (KChoiceMany). */
export function FChoiceManyBlock({
  label,
  values,
  options,
  onChange,
  hint,
}: {
  label: ReactNode;
  values: readonly string[];
  options: readonly { value: string; label: ReactNode; hint?: ReactNode; disabled?: boolean }[];
  onChange: (values: string[]) => void;
  hint?: ReactNode;
}) {
  return (
    <FBlock label={label} hint={hint}>
      <KChoiceMany
        values={values}
        options={options}
        onChange={onChange}
        ariaLabel={typeof label === 'string' ? label : undefined}
      />
    </FBlock>
  );
}

/**
 * ПОЛЕ ВНУТРИ ПУЗЫРЯ (владелец 2026-08-11: «заголовок над пузырём, заголовок над
 * КАЖДЫМ полем»): серый заголовок поля + контрол + подсказка, живёт ТОЛЬКО внутри
 * FBlock/FCard. Пузырь из нескольких полей = FBlock с названием, внутри FIn на каждое.
 * Единственное поле, чьё имя совпадает с названием пузыря (эталон «Кодовые слова»), —
 * без FIn: имя пузыря и есть заголовок поля.
 * ВЫБОР ИЗ СПИСКА в FIn НЕ кладут (владелец 2026-08-11, «Категория»): выбор — это
 * РАСКРЫТЫЙ список с галочкой ОТДЕЛЬНЫМ пузырём (FChoiceBlock), не выпадашка в поле.
 */
export function FIn({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="fk-in">
      <label>{label}</label>
      {children}
      {hint ? <p className="meta">{hint}</p> : null}
      {error ? <FError>{error}</FError> : null}
    </div>
  );
}

/** Пузырь-карточка формы: группа полей на белом. Один пузырь = одна смысловая группа. */
export function FCard({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`card fld-card${className ? ` ${className}` : ''}`}>{children}</div>;
}

/** Строка списка-переходов (FNotifyList): одна на весь кабинет, копий не бывает. */
export interface FNotifyItem {
  key: string;
  label: ReactNode;
  /** Серая сводка текущих настроек («Пуш и почта», «Выключено»). */
  summary?: ReactNode;
  /** Розовая пилюля-цифра справа (непрочитанное у «Полученных»). */
  badge?: ReactNode;
  /** false — строка без шеврона (записи ленты: тап читает, а не «проваливается»). */
  chevron?: boolean;
  /** Значок строки (владелец 12.08: «компонент может иметь, а может не иметь иконку»):
   *  БЕЛЫЙ глиф на НАСЫЩЕННОЙ плитке своего цвета — как разделы админки (владелец
   *  29.08.2026, отменяет бледную плитку с тёмным глифом). Цвет плитки задаёт
   *  iconColor, вид — kit.css. Нет иконки — строка как раньше. */
  icon?: ReactNode;
  iconColor?: string;
  /** Тап по строке. НЕТ — за строкой ничего не стоит (вход без настроек, К16.1 П5):
   *  строка не нажимается вовсе, а не «нажимается и молчит». */
  onOpen?: () => void;
  /** СТРОКА НЕДОСТУПНА (MESSAGE-COMPOSER К18.1 п.3, владелец 06.09.2026: «либо не
   *  показывается, либо неактивная, как правильно по канонам iPhone»): имя серым —
   *  ролью приглушённого текста палитры, своих hex тут нет. Руки у такой строки не
   *  бывает вовсе (`onOpen` не задают), причина стоит в `summary`. */
  off?: boolean;
  /** Убрать эту строку — рукой режима правки (см. `editing`). */
  onRemove?: () => void;
  /** ЗНАЧЕНИЕ СПРАВА серым перед шевроном («1 условие», «Europe / Lisbon») — строка
   *  «подпись · значение · ›» родных списков iOS (ТЗ WEB-PARITY-3, типовая вещь). */
  value?: ReactNode;
  /** КНОПКА СТРОКИ В РЕЖИМЕ ПРАВКИ (QUICK-START §11.16 п.3: пауза письма цепочки — значок часов и «через 1 день»
   *  справа, перед ручкой): тап — своё действие, строка при этом не открывается. Вне правки её нет. */
  editAction?: { label: ReactNode; icon?: ReactNode; onClick: () => void };
  /** ПИЛЮЛЯ ПАУЗЫ СТРОКИ ПИСЬМА (QUICK-START §11.34 п.12б–в): значок часов и короткий код («1Д», «3Ч», «10М»), одна
   *  ширина у всех строк, стоит ВСЕГДА; в режиме правки — мягкий розовый фон и тап `onClick` (полушторка паузы),
   *  строка при этом не перестраивается. */
  pause?: { code: string; icon?: ReactNode; onClick?: () => void };
  /** Значение справа уходит в режиме правки (папки «Ботов», кадр iOS B13-bots-folders-edit:
   *  в «Изм.» строка — минус, плитка, имя и «≡», без числа ботов). */
  valueEditHidden?: boolean;
  /** `accent` — строка-действие «Добавить …»: слово акцентом по центру, без шеврона
   *  (первая строка карточки папок и меток в iOS: B13, C04, K14). */
  tone?: 'accent';
  /** Строка вне режима правки: без минуса и ручки, в перетаскивании не участвует («Добавить …»). */
  fixed?: boolean;
}

/**
 * СПИСОК СТРОК-ПЕРЕХОДОВ В ПУЗЫРЕ (владелец 12.08.2026, вторая правка вида
 * NOTIFY-CENTER: «в одном пузыре три типа, никаких тумблеров — нажимаешь, открывается
 * шторка с подробными настройками»). Один пузырь с именем; внутри строки: имя жирным,
 * под ним серая сводка, справа шеврон; тап по строке открывает её шторку. Все
 * настройки живут в шторке, на строке органов управления НЕТ.
 */
export function FNotifyList({
  label,
  hint,
  foot,
  className,
  items,
  editing,
  removeLabel,
  onReorder,
  hoverTrash = true,
}: {
  /** Название пузыря серым над ним. НЕОБЯЗАТЕЛЬНО (исключение канона, владелец 12.08:
   *  одиночная строка-переход «Полученные» стоит пузырём без подписи — имя несёт сама
   *  строка, подпись сверху дублировала бы его). */
  label?: ReactNode;
  /**
   * ПОДВОДКА РАЗДЕЛА — ПОД ЗАГОЛОВКОМ, НАД строками (MESSAGE-COMPOSER К18.1 п.4 п.4,
   * владелец 06.09.2026 о разделе «Ссылки из ваших ботов»): объяснение, откуда взялись
   * строки и что даст выбор, читается ДО списка. Описание всего пузыря (FBlock hint)
   * этим не отменяется — оно по-прежнему стоит ПОД пузырём.
   */
  hint?: ReactNode;
  /** ПОДПИСЬ ПОД КАРТОЧКОЙ (APP-CLAIMS-1020 ред. 11, Д2/10.7; `footer` секции страницы сервера): серый абзац снаружи
   *  пузыря, тем же рецептом, что «Видно только вам» у поля (`kf-blk__foot`), — не пузырь и не строка. */
  foot?: ReactNode;
  /** Дополнительный класс корня (лента «Полученных» ставит fk-tight — плотный воздух). */
  className?: string;
  items: readonly FNotifyItem[];
  /**
   * РЕЖИМ ПРАВКИ СПИСКА (MESSAGE-COMPOSER К16.1 П6, владелец 06.09.2026: «нажал, и у
   * всех появились кнопки удалить») — приём родных списков iOS (Почта, Напоминания):
   * слева у строки красный кружок с минусом, тап по нему открывает справа «Удалить»,
   * второй тап убирает строку. Это СВОЙСТВО ОДНОГО компонента строки, а не второй
   * список: копий разметки ряда в кабинете не бывает.
   */
  editing?: boolean;
  /** Слово красной кнопки строки («Удалить»). */
  removeLabel?: ReactNode;
  /**
   * ПОРЯДОК СТРОК — ПЕРЕТАСКИВАНИЕМ (П85, владелец 13.09.2026: «зажать и перетянуть;
   * кнопки „Выше/Ниже“ — убрать»). В режиме правки справа у строки ручка; тянут её
   * мышью или пальцем (pointer events, без сторонних библиотек), соседи раздвигаются,
   * отпустили — сюда приходит новый порядок ключей. Нет руки — строки не таскаются.
   */
  onReorder?: (keys: string[]) => void;
  /** Корзина при наведении мышью (десктоп). Списки с «Изм.» (DEditList) её не носят — удаление
   *  там минусом в режиме правки, как в iOS (ТЗ WEB-CAUSES М76). Смахивание пальцем не трогается. */
  hoverTrash?: boolean;
}) {
  return (
    <div className={`fld kf-blk${className ? ` ${className}` : ''}`}>
      {label ? <label className="kf-blk__cap">{label}</label> : null}
      {hint ? <p className="meta kf-blk__lead">{hint}</p> : null}
      <div className="kf-blk__body fk-nlist">
        {/* Ключ по режиму: вышли из правки — ряды пересобираются, и «Удалить» у строки
            гаснет само. Гасить его эффектом не надо (лишний каскад рендеров). */}
        <FNotifyRows
          key={editing ? 'edit' : 'view'}
          items={items}
          {...(editing ? { editing: true } : {})}
          {...(removeLabel != null ? { removeLabel } : {})}
          {...(onReorder ? { onReorder } : {})}
          hoverTrash={hoverTrash}
        />
      </div>
      {foot ? <p className="meta kf-blk__foot">{foot}</p> : null}
    </div>
  );
}

/** Ряды списка-переходов: своей строки разметки у страниц нет — она живёт здесь одна.
 *  Отдельный компонент нужен ради памяти «у какой строки открыто „Удалить“»: смена
 *  режима правки пересобирает ряды по ключу, и память гаснет без эффекта. */
function FNotifyRows({
  items,
  editing,
  removeLabel,
  onReorder,
  hoverTrash,
}: {
  items: readonly FNotifyItem[];
  editing?: boolean;
  removeLabel?: ReactNode;
  onReorder?: (keys: string[]) => void;
  hoverTrash: boolean;
}) {
  // Какая строка сейчас показывает «Удалить» (в родном списке iOS она одна).
  const [armed, setArmed] = useState<string | null>(null);
  // ПЕРЕТАСКИВАНИЕ (П85): какая строка поднята, на сколько уехала за указателем и какой
  // порядок ключей получается прямо сейчас. Прямоугольники строк снимаются в момент
  // захвата — соседи двигаются картинкой (transform), их живые размеры не нужны.
  const [drag, setDrag] = useState<{ key: string; dy: number; order: string[] } | null>(null);
  const rowsRef = useRef(new Map<string, HTMLElement>());
  const startDrag = (key: string, e: React.PointerEvent<HTMLElement>) => {
    if (!onReorder) return;
    e.preventDefault();
    const handle = e.currentTarget;
    const keys = items.filter((it) => !it.fixed).map((it) => it.key);
    const rects = new Map(
      keys.map((k) => [k, rowsRef.current.get(k)?.getBoundingClientRect()] as const),
    );
    const mine = rects.get(key);
    if (!mine) return;
    handle.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    let order = keys;
    const move = (ev: PointerEvent) => {
      const dy = ev.clientY - startY;
      const center = mine.top + mine.height / 2 + dy;
      const others = keys.filter((k) => k !== key);
      // Новое место — сколько ДРУГИХ строк стоит выше поднятой.
      const n = others.filter((k) => {
        const r = rects.get(k);
        return !!r && r.top + r.height / 2 < center;
      }).length;
      order = [...others.slice(0, n), key, ...others.slice(n)];
      setDrag({ key, dy, order });
    };
    const up = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      setDrag(null);
      if (order.join('\u0001') !== keys.join('\u0001')) onReorder(order);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
    setDrag({ key, dy: 0, order: keys });
  };
  /** На сколько строка уехала: поднятая — за указателем, соседи — на высоту поднятой. */
  const shiftOf = (key: string): number => {
    if (!drag || !drag.order.includes(key)) return 0;
    if (key === drag.key) return drag.dy;
    const was = items.findIndex((it) => it.key === key);
    const now = drag.order.indexOf(key);
    if (was === now) return 0;
    const h = rowsRef.current.get(drag.key)?.offsetHeight ?? 0;
    return now < was ? -h : h;
  };
  // СМАХИВАНИЕ ВЛЕВО — «УДАЛИТЬ» (решение владельца 02.10.2026, ТЗ WEB-PARITY-3 3.Б: «на
  // телефоне плюс смахивание влево, на десктопе корзина при наведении; крестиков у строк
  // нет»). Пальцем ведут строку влево — справа выезжает красная плашка, тап по ней убирает
  // строку (вопрос задаёт хозяин списка в onRemove). Одна открытая плашка на список.
  const [swiped, setSwiped] = useState<string | null>(null);
  const [pull, setPull] = useState<{ key: string; dx: number } | null>(null);
  const swipeStart = (key: string, e: React.PointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse') return;
    const x0 = e.clientX;
    const y0 = e.clientY;
    const el = e.currentTarget;
    let live = false;
    let dx = 0;
    const move = (ev: PointerEvent) => {
      dx = ev.clientX - x0;
      const dy = ev.clientY - y0;
      if (!live) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        live = true;
      }
      setPull({ key, dx: Math.max(-SWIPE_W, Math.min(0, dx)) });
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      setPull(null);
      if (live) setSwiped(dx < -SWIPE_W / 2 ? key : null);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };
  return (
    <>
      {items.map((it) => {
        const editable = !!editing && !it.fixed && (!!it.onRemove || !!onReorder);
        const swipeable = !!it.onRemove && !editable;
        const body = (
          <>
            {/* КРАСНЫЙ КРУЖОК С МИНУСОМ (режим правки): сигнальный красный палитры,
                  своих hex тут нет. Тап открывает «Удалить» у этой строки. */}
            {editable && it.onRemove ? (
              <KBareButton
                className="fk-nrow-minus"
                aria-label={typeof removeLabel === 'string' ? removeLabel : undefined}
                onClick={() => setArmed((cur) => (cur === it.key ? null : it.key))}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M6 12h12" />
                </svg>
              </KBareButton>
            ) : null}
            {/* Цвет пункта уходит плитке ролью currentColor: подложка — пастель этого же
                  цвета, глиф — им самим (зеркало AccountTileView приложения, паритет 09.09.2026). */}
            {it.icon ? (
              <span
                className="ic"
                aria-hidden
                {...(it.iconColor ? { style: { color: it.iconColor } } : {})}
              >
                {it.icon}
              </span>
            ) : null}
            <span className="fk-nrow-main">
              <span className="fk-nrow-t">{it.label}</span>
              {it.summary ? <span className="meta fk-nrow-sub">{it.summary}</span> : null}
            </span>
            {it.value != null && it.value !== '' && !(editable && it.valueEditHidden) ? (
              <span className="fk-nrow-val">{it.value}</span>
            ) : null}
            {it.badge != null ? <span className="fk-nlist-badge">{it.badge}</span> : null}
            {editable && armed === it.key ? (
              <KBareButton className="fk-nrow-del" onClick={() => it.onRemove?.()}>
                {removeLabel}
              </KBareButton>
            ) : null}
            {/* КОРЗИНА ПРИ НАВЕДЕНИИ — только мышь (решение владельца 02.10.2026): на
                  десктопе смахнуть нечем. Видна по :hover строки (kit.css). */}
            {swipeable && hoverTrash ? (
              <KBareButton
                className="fk-nrow-trash"
                aria-label={typeof removeLabel === 'string' ? removeLabel : undefined}
                title={typeof removeLabel === 'string' ? removeLabel : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  it.onRemove?.();
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                </svg>
              </KBareButton>
            ) : null}
            {editable && it.editAction ? (
              <KBareButton
                className="fk-nrow-act"
                onClick={(e) => {
                  e.stopPropagation();
                  it.editAction?.onClick();
                }}
              >
                {it.editAction.icon ?? null}
                <span>{it.editAction.label}</span>
              </KBareButton>
            ) : null}
            {it.pause ? (
              editable && it.pause.onClick ? (
                <KBareButton
                  className="fk-nrow-pause is-edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    it.pause?.onClick?.();
                  }}
                >
                  {it.pause.icon ?? null}
                  <span>{it.pause.code}</span>
                </KBareButton>
              ) : (
                <span className="fk-nrow-pause">
                  {it.pause.icon ?? null}
                  <span>{it.pause.code}</span>
                </span>
              )
            ) : null}
            {/* РУЧКА ПЕРЕТАСКИВАНИЯ (П85): три полоски справа, как у списков iOS; тянут
                  именно её, чтобы тап по строке и прокрутка пальцем не спорили с ходом. */}
            {editable && onReorder ? (
              <span
                className="fk-nrow-grip"
                aria-hidden
                onPointerDown={(e) => startDrag(it.key, e)}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </span>
            ) : null}
            {/* Шеврон остаётся и в режиме правки (кадр iOS K14-tags-edit): строка с настройками
                  открывается и там; пропадает только там, где его место заняла ручка. */}
            {it.chevron === false || it.tone === 'accent' || (editable && onReorder) ? null : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
                className="fk-nlist-chev"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            )}
          </>
        );
        const shift = shiftOf(it.key);
        const lifted = drag?.key === it.key;
        const open = swipeable && swiped === it.key;
        const dx = pull?.key === it.key ? pull.dx : open ? -SWIPE_W : 0;
        const cls = `fk-nlist-row${it.summary ? ' has-sub' : ''}${it.icon ? ' has-ic' : ''}${it.tone === 'accent' ? ' is-accent' : ''}${editable ? ' is-edit' : ''}${it.off ? ' is-off' : ''}${lifted ? ' is-drag' : drag ? ' is-shift' : ''}${swipeable ? ' can-swipe' : ''}${open ? ' is-swiped' : ''}${pull?.key === it.key ? ' is-pull' : ''}`;
        // Смещение строки при перетаскивании — число из хода указателя, оно и есть стиль.
        const style = shift ? { transform: `translateY(${shift}px)` } : undefined;
        // ВЫСОТА СТРОКИ ОДНА НА КАБИНЕТ (И10 п.2, владелец 04.09.2026): без подстроки
        // ряд ровно 52, с подстрокой растёт по содержимому. Что это за случай, знает
        // только компонент — он и вешает класс, а не страница.
        //
        // Строка БЕЗ руки (вход без настроек, К16.1 П5) кнопкой не бывает: нажимаемое
        // место, которое ничего не делает, врёт человеку.
        if (swipeable) {
          // Строка со смахиванием: содержимое едет влево, за ним красная плашка.
          // Внутри — кнопки (корзина, плашка), поэтому сама строка не <button>.
          const openRow = it.onOpen;
          return (
            <div
              key={it.key}
              className={cls}
              {...(openRow ? { role: 'button', tabIndex: 0 } : {})}
              onPointerDown={(e) => swipeStart(it.key, e)}
              onClick={() => {
                if (open) {
                  setSwiped(null);
                  return;
                }
                openRow?.();
              }}
              onKeyDown={(e) => {
                if (
                  openRow &&
                  (e.key === 'Enter' || e.key === ' ') &&
                  e.target === e.currentTarget
                ) {
                  e.preventDefault();
                  openRow();
                }
              }}
              ref={(el) => {
                if (el) rowsRef.current.set(it.key, el);
                else rowsRef.current.delete(it.key);
              }}
            >
              <span
                className="fk-nrow-in"
                style={dx ? { transform: `translateX(${dx}px)` } : undefined}
              >
                {body}
              </span>
              <KBareButton
                className="fk-nrow-swipe"
                tabIndex={open ? 0 : -1}
                aria-hidden={!open}
                onClick={(e) => {
                  e.stopPropagation();
                  setSwiped(null);
                  it.onRemove?.();
                }}
              >
                {removeLabel}
              </KBareButton>
            </div>
          );
        }
        return it.onOpen && !editable ? (
          <KBareButton key={it.key} className={cls} onClick={it.onOpen}>
            {body}
          </KBareButton>
        ) : (
          <div
            key={it.key}
            className={cls}
            {...(style ? { style } : {})}
            ref={(el) => {
              if (el) rowsRef.current.set(it.key, el);
              else rowsRef.current.delete(it.key);
            }}
          >
            {body}
          </div>
        );
      })}
    </>
  );
}

/** Ширина красной плашки «Удалить» за смахнутой строкой (родная плашка iOS ~ 88). */
const SWIPE_W = 88;

/**
 * СПИСОК «ГАЛОЧКА + ПЕРЕХОД» (MESSAGE-COMPOSER И9.2, владелец 04.09.2026): строка
 * отмечается галочкой СЛЕВА, а тап по имени открывает СВОЮ страницу с настройками этой
 * строки. Два разных действия в одной строке — поэтому и компонент отдельный: у
 * KChoiceMany вся строка это одна кнопка-галочка, а у FNotifyList галочки нет вовсе, и
 * склеить их разметкой по месту было бы ровно тем зоопарком, ради которого заведён набор.
 *
 * Где живёт: список ботов условия «Прошёл бота» (галочка отмечает бота, имя ведёт на его
 * настройки «как прошёл / когда проходил / убрать бота»). Пояснения — подвалом группы,
 * внутри строки их не бывает (канон родных списков iOS); `sub` — это ЗНАЧЕНИЕ строки
 * («дошёл до цели · 1–7 сент.»), а не пояснение.
 */
export function FPickNavList({
  label,
  hint,
  items,
}: {
  label?: ReactNode;
  /** Подвал группы — пояснение или слова пустого списка. */
  hint?: ReactNode;
  items: readonly {
    key: string;
    label: ReactNode;
    /** Значение строки серым под именем (настройки отмеченного). */
    sub?: ReactNode;
    checked: boolean;
    /** Подпись галочки для VoiceOver. */
    pickLabel: string;
    onToggle: () => void;
    /** Тап по имени: своя страница. Нет — имя не нажимается (строка только отмечается). */
    onOpen?: () => void;
  }[];
}) {
  return (
    <div className="fld kf-blk">
      {label ? <label className="kf-blk__cap">{label}</label> : null}
      <div className="kf-blk__body fk-nlist">
        {items.map((it) => (
          <div key={it.key} className="fk-picknav">
            <KBareButton
              className="fk-picknav__box"
              role="checkbox"
              aria-checked={it.checked}
              aria-label={it.pickLabel}
              onClick={it.onToggle}
            >
              <svg
                className={`kf-ch__tick${it.checked ? '' : ' is-off'}`}
                width="19"
                height="19"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.3}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M4.5 12.5l5 5 10-11" />
              </svg>
            </KBareButton>
            {it.onOpen ? (
              <KBareButton
                className={`fk-nlist-row fk-picknav__main${it.sub ? ' has-sub' : ''}`}
                onClick={it.onOpen}
              >
                <span className="fk-nrow-main">
                  <span className="fk-nrow-t">{it.label}</span>
                  {it.sub ? <span className="meta fk-nrow-sub">{it.sub}</span> : null}
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                  className="fk-nlist-chev"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </KBareButton>
            ) : (
              <KBareButton
                className={`fk-nlist-row fk-picknav__main${it.sub ? ' has-sub' : ''}`}
                onClick={it.onToggle}
              >
                <span className="fk-nrow-main">
                  <span className="fk-nrow-t">{it.label}</span>
                  {it.sub ? <span className="meta fk-nrow-sub">{it.sub}</span> : null}
                </span>
              </KBareButton>
            )}
          </div>
        ))}
      </div>
      {hint ? <p className="meta kf-blk__note">{hint}</p> : null}
    </div>
  );
}

/** Короткий серый текст: вступление формы, пояснение под полем, любая подпись-серым.
 *  Отдельных «своих» серых абзацев на страницах не заводить. */
/**
 * СТРОКА СО СМАХИВАНИЕМ ВЛЕВО → «Удалить» (решение владельца 02.10.2026 по ТЗ WEB-PARITY-3:
 * у строк нет ни крестиков, ни «⋯» — на телефоне смахивание влево, как в iOS). Один
 * компонент на все списки с удалением строк (метки человека, варианты ответа, …):
 * тянется содержимое, под ним открывается красная плашка; тап по ней — `onDelete`.
 * Мышью (десктоп) смахнуть нечем — там удаляет режим правки списка (минус/корзина).
 */
export function FSwipeRow({
  children,
  onDelete,
  deleteLabel,
  className,
}: {
  children: ReactNode;
  onDelete: () => void;
  deleteLabel: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const touch = useRef<{ x: number; y: number; dx: number } | null>(null);
  const inner = useRef<HTMLDivElement>(null);
  const W = 88;
  return (
    <div
      className={`fk-swipe${open ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
      onTouchStart={(e) => {
        const t0 = e.touches[0];
        if (t0) touch.current = { x: t0.clientX, y: t0.clientY, dx: 0 };
      }}
      onTouchMove={(e) => {
        const t0 = e.touches[0];
        const st = touch.current;
        if (!t0 || !st) return;
        const dx = t0.clientX - st.x;
        if (Math.abs(t0.clientY - st.y) > Math.abs(dx)) return;
        st.dx = dx;
        if (inner.current)
          inner.current.style.transform = `translateX(${Math.max(-W, Math.min(0, (open ? -W : 0) + dx))}px)`;
      }}
      onTouchEnd={() => {
        const st = touch.current;
        touch.current = null;
        if (inner.current) inner.current.style.transform = '';
        if (!st) return;
        setOpen((open ? -W : 0) + st.dx < -W / 2);
      }}
    >
      <KBareButton
        className="fk-swipe-del"
        onClick={() => {
          setOpen(false);
          onDelete();
        }}
      >
        {deleteLabel}
      </KBareButton>
      <div className="fk-swipe-in" ref={inner}>
        {children}
      </div>
    </div>
  );
}

export function FHint({ children, className }: { children: ReactNode; className?: string }) {
  // Со своим классом отступы задаёт CSS класса (подсказка пустого списка, абзац-инструкция).
  return (
    <p
      className={`meta${className ? ` ${className}` : ''}`}
      style={{ ...(className ? {} : { margin: 0 }), lineHeight: 1.5 }}
    >
      {children}
    </p>
  );
}

/**
 * КАРТИНКА С КРЕСТИКОМ В УГЛУ — один компонент на кабинет (владелец 02.10.2026, К3.60:
 * «картинку убрать нельзя» → решение «крестик в углу, без странных решений»). Миниатюра
 * 46 со скруглением --r-sm (кадр iOS W14-igkw-step5-picture), в правом верхнем углу
 * тёмный кружок с белым крестиком — им и убирают. Тап по самой картинке — `onTap`
 * (заменить / открыть). Где стоит: картинка приветствия мастера, картинка рассылки,
 * строка «Сейчас в сообщении», фото профиля — своей копии «крестик у картинки» не бывает.
 */
export function FThumb({
  src,
  onRemove,
  removeLabel,
  onTap,
  tapLabel,
  className,
}: {
  src: string;
  onRemove: () => void;
  removeLabel: string;
  onTap?: () => void;
  tapLabel?: string;
  className?: string;
}) {
  return (
    <span className={`fk-thumb${className ? ` ${className}` : ''}`}>
      {onTap ? (
        <KBareButton type="button" className="fk-thumb__pic" onClick={onTap} aria-label={tapLabel}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img decoding="async" loading="lazy" src={src} alt="" />
        </KBareButton>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img decoding="async" loading="lazy" src={src} alt="" className="fk-thumb__pic" />
      )}
      <KBareButton
        type="button"
        className="fk-thumb__x"
        onClick={onRemove}
        aria-label={removeLabel}
        title={removeLabel}
      >
        <svg
          viewBox="0 0 24 24"
          width={12}
          height={12}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.6}
          strokeLinecap="round"
          aria-hidden
        >
          <path d="m7 7 10 10M17 7 7 17" />
        </svg>
      </KBareButton>
    </span>
  );
}

/** Красная строка ошибки поля (не рамка: рамок в форме нет вовсе). */
export function FError({ children }: { children: ReactNode }) {
  return <p className="scn-err-field">{children}</p>;
}

/**
 * Ошибка ВСЕГО ОКНА (владелец 2026-08-14): одна короткая красная строка ПО ЦЕНТРУ первой
 * строкой тела — «что мешает нажать галочку». Не в шапке: в приложении шапку рисует натив,
 * писать в неё нельзя, поэтому строка живёт в теле сразу под заголовком. Показывать её
 * можно ТОЛЬКО после попытки применить (до первой попытки красного в окне нет вовсе) —
 * решение владельца 06.08, распространённое 14.08 на все окна кабинета.
 * Один компонент на весь кабинет: своих красных строк по месту не заводить.
 */
export function FFormError({ children, flush }: { children: ReactNode; flush?: boolean }) {
  return <p className={`scn-err-form${flush ? ' flush' : ''}`}>{children}</p>;
}

/** Поле формы: подпись сверху, значение под ней, под ним подсказка и ошибка.
 *  `children` — сам контрол (input/textarea/SmartSelect/свой контрол).
 *  `flush` — без нижнего отступа .fld (поля внутри карточки-пузыря: воздух даёт карточка).
 *  `bad` — поле не заполнено: подсвечивается тем же классом, что общий рендерер. */
export function FField({
  label,
  hint,
  error,
  bad,
  flush,
  cap,
  className,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  bad?: boolean;
  flush?: boolean;
  /** Подпись НАД пузырём (структура kf-blk, как у полей рендерера в мастере): подпись
   *  серым снаружи, значение и подсказка — внутри пузыря kf-blk__body. Канон 11.08.2026
   *  «заголовок у пузыря и у инпута — каждый раз»: свои блоки мастера обязаны выглядеть
   *  как поля рендерера, пузырь-целиком-с-подписью-внутри запрещён. */
  cap?: boolean;
  className?: string;
  children: ReactNode;
}) {
  if (cap) {
    return (
      <div
        className={`fld kf-blk${bad ? ' wiz-bad' : ''}${className ? ` ${className}` : ''}`}
        {...(flush ? { style: { margin: 0 } } : {})}
      >
        {label ? <label className="kf-blk__cap">{label}</label> : null}
        <div className="kf-blk__body">{children}</div>
        {/* Подсказка — снаружи под пузырём (кадры iOS W14/W15, общий рецепт поля мастера). */}
        {hint ? <p className="meta kf-blk__foot">{hint}</p> : null}
        {error ? <FError>{error}</FError> : null}
      </div>
    );
  }
  return (
    <div
      className={`fld${bad ? ' wiz-bad' : ''}${className ? ` ${className}` : ''}`}
      {...(flush ? { style: { margin: 0 } } : {})}
    >
      {label ? <label>{label}</label> : null}
      {children}
      {hint ? <p className="meta">{hint}</p> : null}
      {error ? <FError>{error}</FError> : null}
    </div>
  );
}

/** ПОЛЕ-КАРТОЧКА С ОЧИСТКОЙ (одна типовая вещь — один компонент, ТЗ WEB-PARITY-3 3.Б; кадры iOS
 *  K02-profile-name-edit и B09-botwin-name): на телефоне в браузере — белая карточка 72, значение
 *  17, серый кружок «×» справа стирает слово и возвращает курсор в поле; на широком экране —
 *  обычное поле набора без «×» (в приложении такие листы рисует натив). Числа — cabinet.css
 *  `.f-clear-*`. Прежняя копия жила только у поля «Имя» профиля (`.prof-name-*`). */
export const FClearField = forwardRef<
  HTMLInputElement,
  {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    maxLength?: number;
    autoCapitalize?: string;
    autoFocus?: boolean;
    ariaLabel?: string;
    onEnter?: () => void;
  }
>(function FClearField(
  { value, onChange, placeholder, maxLength, autoCapitalize, autoFocus, ariaLabel, onEnter },
  outer,
) {
  const inner = useRef<HTMLInputElement | null>(null);
  const setRef = (el: HTMLInputElement | null): void => {
    inner.current = el;
    if (typeof outer === 'function') outer(el);
    else if (outer) outer.current = el;
  };
  return (
    <div className="f-clear-field">
      <KInput
        ref={setRef}
        className="fld-solo f-clear-in"
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        autoCapitalize={autoCapitalize}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onEnter?.();
        }}
      />
      {value ? (
        <KBareButton
          className="f-clear-x"
          aria-label="×"
          onClick={() => {
            onChange('');
            inner.current?.focus();
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.6}
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </KBareButton>
      ) : null}
    </div>
  );
});

/** ПОЛЕ С ОЧИСТКОЙ ВО ВРЕМЯ ПРАВКИ (clearButtonMode = .whileEditing у родного UITextField; круг 3,
 *  Н6 п.7, кадры iOS M20-button-page / M25-button-url-error): то же поле набора `KInput`, а пока
 *  каретка в нём и оно не пустое — справа серый кружок «×» 16, центр в 31 от правой кромки
 *  карточки; тап стирает значение и оставляет каретку в поле. Вид поля не меняется — обёртка
 *  только кладёт кружок поверх. Числа — cabinet.css `.f-clear-edit`. На широком экране кружка нет
 *  (как у FClearField). Отступ кружка от края поля — `--f-clear-r` у обёртки (7: поле стоит в
 *  карточке с полем 16). */
export function FClearInput({
  value,
  onClear,
  ref: outer,
  ...rest
}: React.ComponentProps<typeof KInput> & { value: string; onClear: () => void }) {
  const inner = useRef<HTMLInputElement | null>(null);
  return (
    <span className="f-clear-edit">
      <KInput
        {...rest}
        value={value}
        ref={(el: HTMLInputElement | null) => {
          inner.current = el;
          if (typeof outer === 'function') outer(el);
          else if (outer) (outer as { current: HTMLInputElement | null }).current = el;
        }}
      />
      {value ? (
        <KBareButton
          className="f-clear-x f-clear-x--edit"
          aria-label="×"
          tabIndex={-1}
          // Фокус остаётся в поле: иначе кружок пропадал бы вместе с фокусом до клика.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onClear();
            inner.current?.focus();
          }}
        >
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.4} strokeLinecap="round" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </KBareButton>
      ) : null}
    </span>
  );
}
